// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { 
  ArrowLeft, 
  Edit, 
  RotateCcw, 
  Save, 
  Download,
  FileText,
  Presentation,
  BookOpen,
  Trash2
} from 'lucide-react';
import { EnhancedExercise } from '../types/exercise';
import { CRISIS_PRINCIPLES } from '../types/wizard';
import { getAudienceType, shouldShowRoleBriefs, getSiteParticipantRoles } from '../utils/audienceInference';
import { getEnhancedFacilitatorScript, hasEnhancedFields, getDisplayTiming, getCurveballTiming } from '../utils/facilitatorMapping';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { ValidationBadge } from './ValidationBadge';
import { exerciseService } from '../services/exerciseService';

export const PreviewEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [exercise, setExercise] = useState<EnhancedExercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState<number | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState('scenario');
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadExercise();
  }, [id, user]);

  const loadExercise = async () => {
    if (!id || !user) return;

    try {
      setLoading(true);
      const exerciseDoc = await getDoc(doc(db, 'c3_exercises', id));
      
      if (!exerciseDoc.exists()) {
        throw new Error('Exercise not found');
      }

      const data = exerciseDoc.data() as EnhancedExercise;
      if (data.ownerUid !== user.uid) {
        throw new Error('Unauthorized access');
      }

      setExercise({ ...data, id: exerciseDoc.id });
    } catch (error: any) {
      setError(error.message);
      console.error('Error loading exercise:', error);
    } finally {
      setLoading(false);
    }
  };

  const regenerateInject = async (injectIndex: number, newPrincipleKey: string) => {
    if (!exercise || !user) return;

    try {
      setRegenerating(injectIndex);
      setError('');

      const regenerateInject = httpsCallable(functions, 'regenerateInject', {
        timeout: 300000 // 5 minutes timeout
      });
      const result = await regenerateInject({
        exerciseId: exercise.id,
        injectIndex,
        principleKey: newPrincipleKey,
        principleLabel: CRISIS_PRINCIPLES[newPrincipleKey]
      });

      if (result.data.success) {
        // Update local state
        const updatedExercise = { ...exercise };
        updatedExercise.sections.injects[injectIndex] = result.data.inject;
        setExercise(updatedExercise);
      }

    } catch (error: any) {
      setError(error.message || 'Failed to regenerate inject');
      console.error('Error regenerating inject:', error);
    } finally {
      setRegenerating(null);
    }
  };

  const exportExercise = async (format: string) => {
    if (!exercise || !user) return;

    try {
      setExporting(format);
      setError('');

      if (format === 'pdf') {
        // Generate PDF client-side using jsPDF
        await generatePDF();
      } else if (format === 'pptx') {
        // Generate PowerPoint via backend service
        await generatePowerPoint();
      } else {
        setError(`${format.toUpperCase()} export not yet implemented`);
      }

    } catch (error: any) {
      setError(error.message || 'Failed to export exercise');
      console.error('Error exporting exercise:', error);
    } finally {
      setExporting(null);
    }
  };

  const generatePowerPoint = async () => {
    if (!exercise || !user) return;

    try {
      // Call backend export function
      const exportExerciseFunction = httpsCallable(functions, 'exportExercise', {
        timeout: 300000 // 5 minutes timeout
      });
      
      const result = await exportExerciseFunction({
        exerciseId: exercise.id,
        format: 'pptx'
      });

      if (result.data.success && result.data.downloadUrl) {
        // Create temporary download link
        const link = document.createElement('a');
        link.href = result.data.downloadUrl;
        link.download = `${exercise.exerciseTitle || 'exercise'}-slides.pptx`;
        link.target = '_blank';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Show success message
        console.log('PowerPoint generated:', result.data.message);
      } else {
        throw new Error(result.data.message || 'Failed to generate PowerPoint');
      }
    } catch (error: any) {
      throw new Error(`PowerPoint generation failed: ${error.message}`);
    }
  };

  const generatePDF = async () => {
    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF();
    
    // Set up fonts and colors
    const primaryColor = '#2563eb';
    const textColor = '#374151';
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    
    let yPosition = 30;
    
    // Header
    pdf.setFontSize(24);
    pdf.setTextColor(primaryColor);
    pdf.text('Crisis Compass', margin, yPosition);
    yPosition += 15;
    
    pdf.setFontSize(16);
    pdf.setTextColor(textColor);
    pdf.text('Crisis Exercise Pack', margin, yPosition);
    yPosition += 20;
    
    // Exercise Title
    pdf.setFontSize(18);
    pdf.setTextColor(primaryColor);
    pdf.text(exercise.exerciseTitle || 'Crisis Exercise', margin, yPosition);
    yPosition += 15;
    
    // Basic Info
    pdf.setFontSize(12);
    pdf.setTextColor(textColor);
    const basicInfo = [
      `Industry: ${exercise.industry}`,
      `Geography: ${exercise.geography}`,
      `Audience: ${exercise.audience}`,
      `Scenario: ${exercise.scenarioType}`,
      `Duration: ${exercise.durationMinutes} minutes`,
      `Generated: ${new Date().toLocaleDateString()}`
    ];
    
    basicInfo.forEach((info) => {
      pdf.text(info, margin, yPosition);
      yPosition += 8;
    });
    
    yPosition += 10;
    
    // Scenario Summary
    if (exercise.sections?.scenarioSummary) {
      pdf.setFontSize(14);
      pdf.setTextColor(primaryColor);
      pdf.text('Scenario Summary', margin, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(textColor);
      const summaryLines = pdf.splitTextToSize(exercise.sections.scenarioSummary, contentWidth);
      pdf.text(summaryLines, margin, yPosition);
      yPosition += summaryLines.length * 6 + 10;
    }
    
    // Injects
    if (exercise.sections?.injects && exercise.sections.injects.length > 0) {
      pdf.setFontSize(14);
      pdf.setTextColor(primaryColor);
      pdf.text('Exercise Injects', margin, yPosition);
      yPosition += 10;
      
      exercise.sections.injects.forEach((inject: any, index: number) => {
        // Check if we need a new page
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 30;
        }
        
        pdf.setFontSize(12);
        pdf.setTextColor(primaryColor);
        pdf.text(`Inject ${inject.order}: ${inject.title}`, margin, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(10);
        pdf.setTextColor(textColor);
        const injectLines = pdf.splitTextToSize(inject.injectText, contentWidth);
        pdf.text(injectLines, margin, yPosition);
        yPosition += injectLines.length * 6 + 5;
        
        if (inject.teachingPoint) {
          pdf.setTextColor('#059669');
          pdf.text('Teaching Point:', margin, yPosition);
          yPosition += 6;
          pdf.setTextColor(textColor);
          const teachingLines = pdf.splitTextToSize(inject.teachingPoint, contentWidth);
          pdf.text(teachingLines, margin, yPosition);
          yPosition += teachingLines.length * 6 + 8;
        }
      });
    }
    
    // Save the PDF
    const fileName = `${exercise.exerciseTitle || 'Crisis-Exercise'}-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  const updateExerciseSection = async (section: string, content: any) => {
    if (!exercise || !user) return;

    try {
      setSaving(true);
      const updatedSections = { ...exercise.sections, [section]: content };
      
      await updateDoc(doc(db, 'c3_exercises', exercise.id), {
        sections: updatedSections,
        updatedAt: new Date()
      });

      setExercise({ ...exercise, sections: updatedSections });
    } catch (error: any) {
      setError(error.message || 'Failed to save changes');
      console.error('Error updating exercise:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExercise = async () => {
    if (!exercise || !user) return;

    try {
      setDeleting(true);
      await exerciseService.deleteExercise(exercise.id);
      setShowDeleteModal(false);
      // Navigate back to dashboard
      navigate('/');
    } catch (error: any) {
      setError(error.message || 'Failed to delete exercise');
      console.error('Error deleting exercise:', error);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading exercise...</div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ fontSize: '18px', color: '#dc2626' }}>
          {error || 'Exercise not found'}
        </div>
      </div>
    );
  }

  // Audience inference for conditional rendering
  const audienceType = getAudienceType(exercise.sections?.meta, exercise.sections?.roleBriefs);
  const showRoleBriefs = shouldShowRoleBriefs(audienceType);

  const allSections = [
    { id: 'scenario', label: 'Scenario Summary', icon: BookOpen },
    { id: 'injects', label: 'Exercise Injects', icon: Edit },
    { id: 'roleBriefs', label: 'Role Briefs', icon: FileText },
    { id: 'scripts', label: 'Facilitator Scripts', icon: Presentation },
    { id: 'debrief', label: 'Debrief Prompts', icon: Edit },
    { id: 'export', label: 'Export Options', icon: Download }
  ];

  // Filter navigation sections based on audience gating
  const filteredSections = allSections.filter(section => {
    if (section.id === 'roleBriefs' && !showRoleBriefs) {
      return false;
    }
    return true;
  });

  // Remove redundant filtering - use filteredSections

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#2563eb',
        color: 'white',
        padding: '20px 0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button 
                onClick={() => navigate('/')}
                style={{
                  marginRight: '16px',
                  backgroundColor: 'white',
                  color: '#2563eb',
                  border: '1px solid white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ArrowLeft style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                Back to Dashboard
              </Button>
              <div>
                <h1 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  margin: '0 0 4px 0'
                }}>Preview & Edit Exercise</h1>
                <p style={{
                  fontSize: '14px',
                  opacity: '0.9',
                  margin: '0'
                }}>
                  {exercise.scenarioType} • {exercise.audience} • {exercise.geography}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <Button
                onClick={() => exportExercise('pdf')}
                disabled={exporting === 'pdf'}
                style={{
                  padding: '8px 16px',
                  backgroundColor: exporting === 'pdf' ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: exporting === 'pdf' ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
              </Button>
              
              <Button
                onClick={() => exportExercise('pptx')}
                disabled={exporting === 'pptx'}
                style={{
                  padding: '8px 16px',
                  backgroundColor: exporting === 'pptx' ? '#9ca3af' : '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: exporting === 'pptx' ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {exporting === 'pptx' ? 'Exporting...' : 'Export PowerPoint'}
              </Button>

              <Button
                onClick={() => setShowDeleteModal(true)}
                disabled={deleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: deleting ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Trash2 style={{ width: '16px', height: '16px' }} />
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '32px 20px',
        display: 'grid',
        gridTemplateColumns: '300px 1fr',
        gap: '32px'
      }}>
        {/* Left Navigation */}
        <div>
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            position: 'sticky',
            top: '32px'
          }}>
            <CardHeader style={{ padding: '20px 20px 16px 20px' }}>
              <CardTitle style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0'
              }}>Exercise Sections</CardTitle>
            </CardHeader>
            <CardContent style={{ padding: '0 0 20px 0' }}>
              {filteredSections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 20px',
                      border: 'none',
                      backgroundColor: selectedSection === section.id ? '#eff6ff' : 'transparent',
                      color: selectedSection === section.id ? '#2563eb' : '#374151',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '14px',
                      borderLeft: selectedSection === section.id ? '3px solid #2563eb' : '3px solid transparent'
                    }}
                  >
                    <Icon style={{ width: '16px', height: '16px' }} />
                    {section.label}
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Right Content */}
        <div>
          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: '6px',
              marginBottom: '24px'
            }}>
              {error}
            </div>
          )}

          {/* Scenario Summary */}
          {selectedSection === 'scenario' && (
            <Card style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
                <CardTitle style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#111827',
                  margin: '0 0 8px 0'
                }}>Scenario Summary</CardTitle>
                <CardDescription style={{
                  color: '#6b7280',
                  fontSize: '14px',
                  margin: '0'
                }}>Overview of the crisis scenario</CardDescription>
              </CardHeader>
              <CardContent style={{ padding: '0 24px 24px 24px' }}>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: '#374151'
                }}>
                  {exercise.sections.scenarioSummary}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Exercise Injects */}
          {selectedSection === 'injects' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {exercise.sections.injects?.filter(inject => inject && inject.order).map((inject, index) => (
                <Card key={inject.order || index} style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <CardHeader style={{ padding: '20px 24px 16px 24px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <CardTitle style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#111827',
                          margin: '0 0 4px 0'
                        }}>
                          Inject {inject.order || index + 1} (T+{inject.timeOffsetMin || 0} min)
                        </CardTitle>
                        <div style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          backgroundColor: '#2563eb',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {inject.principleLabel}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <ValidationBadge 
                          inject={inject}
                          onFix={() => regenerateInject(index, inject.principleKey)}
                        />
                        <select
                          value={inject.principleKey}
                          onChange={(e) => regenerateInject(index, e.target.value)}
                          disabled={regenerating === index}
                          style={{
                            padding: '6px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: 'white'
                          }}
                        >
                          {Object.entries(CRISIS_PRINCIPLES).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                        <Button
                          onClick={() => regenerateInject(index, inject.principleKey)}
                          disabled={regenerating === index}
                          style={{
                            padding: '6px 8px',
                            backgroundColor: regenerating === index ? '#9ca3af' : '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: regenerating === index ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          <RotateCcw style={{ width: '14px', height: '14px' }} />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent style={{ padding: '0 24px 24px 24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          margin: '0 0 8px 0'
                        }}>Inject Content</h4>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          {inject.injectText}
                        </div>
                      </div>

                      <div>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          margin: '0 0 8px 0'
                        }}>Teaching Point</h4>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#eff6ff',
                          borderRadius: '6px',
                          border: '1px solid #bfdbfe',
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          {inject.teachingPoint}
                        </div>
                      </div>

                      <div>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          margin: '0 0 8px 0'
                        }}>Key Dilemmas</h4>
                        <ul style={{
                          margin: '0',
                          paddingLeft: '20px',
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          {inject.dilemmas?.map((dilemma, i) => (
                            <li key={i} style={{ marginBottom: '4px' }}>{dilemma}</li>
                          ))}
                        </ul>
                      </div>

                      {inject.expectedActions && inject.expectedActions.length > 0 && (
                        <div>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#111827',
                            margin: '0 0 8px 0'
                          }}>Expected Actions</h4>
                          <ul style={{
                            margin: '0',
                            paddingLeft: '20px',
                            fontSize: '14px',
                            lineHeight: '1.5'
                          }}>
                            {inject.expectedActions?.map((action, i) => (
                              <li key={i} style={{ marginBottom: '4px' }}>{action}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Decision Thresholds */}
                      {inject.decisionThresholds && inject.decisionThresholds.length > 0 && (
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#fff7ed',
                          border: '1px solid #fed7aa',
                          borderRadius: '6px'
                        }}>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#9a3412',
                            margin: '0 0 8px 0'
                          }}>Thresholds</h4>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {inject.decisionThresholds.map((threshold, i) => (
                              <span key={i} style={{
                                fontSize: '12px',
                                backgroundColor: '#fed7aa',
                                color: '#9a3412',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontWeight: '500'
                              }}>
                                {threshold}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Capture To */}
                      {inject.captureTo && inject.captureTo.length > 0 && (
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '6px'
                        }}>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#166534',
                            margin: '0 0 8px 0'
                          }}>Capture to</h4>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {inject.captureTo.map((target, i) => (
                              <span key={i} style={{
                                fontSize: '12px',
                                backgroundColor: '#bbf7d0',
                                color: '#166534',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontWeight: '500'
                              }}>
                                {target}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Export Options */}
          {selectedSection === 'export' && (
            <Card style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
                <CardTitle style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#111827',
                  margin: '0 0 8px 0'
                }}>Export Options</CardTitle>
                <CardDescription style={{
                  color: '#6b7280',
                  fontSize: '14px',
                  margin: '0'
                }}>Download your exercise in different formats</CardDescription>
              </CardHeader>
              <CardContent style={{ padding: '0 24px 24px 24px' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px'
                }}>
                  <Button
                    onClick={() => exportExercise('pdf')}
                    disabled={exporting === 'pdf'}
                    style={{
                      padding: '16px',
                      backgroundColor: exporting === 'pdf' ? '#f3f4f6' : 'white',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: exporting === 'pdf' ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FileText style={{ width: '24px', height: '24px', color: '#dc2626' }} />
                    <span style={{ fontWeight: '500' }}>
                      {exporting === 'pdf' ? 'Generating...' : 'PDF Facilitator Pack'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      Complete exercise guide with branding
                    </span>
                  </Button>

                  <Button
                    onClick={() => exportExercise('pptx')}
                    disabled={exporting === 'pptx'}
                    style={{
                      padding: '16px',
                      backgroundColor: exporting === 'pptx' ? '#f3f4f6' : 'white',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: exporting === 'pptx' ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Presentation style={{ width: '24px', height: '24px', color: '#f59e0b' }} />
                    <span style={{ fontWeight: '500' }}>
                      {exporting === 'pptx' ? 'Generating...' : 'PowerPoint Slides'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      Slide deck for presentation
                    </span>
                  </Button>

                  <Button
                    onClick={() => exportExercise('docx')}
                    disabled={exporting === 'docx'}
                    style={{
                      padding: '16px',
                      backgroundColor: exporting === 'docx' ? '#f3f4f6' : 'white',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: exporting === 'docx' ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Edit style={{ width: '24px', height: '24px', color: '#2563eb' }} />
                    <span style={{ fontWeight: '500' }}>
                      {exporting === 'docx' ? 'Generating...' : 'Word Document'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      Editable document format
                    </span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Other sections would go here - roleBriefs, scripts, debrief */}
          {selectedSection === 'roleBriefs' && showRoleBriefs && (
            <Card style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
                <CardTitle style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#111827',
                  margin: '0'
                }}>Role Briefs</CardTitle>
              </CardHeader>
              <CardContent style={{ padding: '0 24px 24px 24px' }}>
                {exercise.sections?.roleBriefs && exercise.sections.roleBriefs.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {exercise.sections.roleBriefs.map((brief: any, index: number) => (
                      <div key={index} style={{
                        padding: '16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1e293b',
                          margin: '0 0 12px 0'
                        }}>{brief.role}</h3>
                        <div style={{ marginBottom: '12px' }}>
                          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 4px 0' }}>Purpose:</h4>
                          <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>{brief.purpose}</div>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 4px 0' }}>First Moves:</h4>
                          <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            {brief.firstMoves?.map((move: string, idx: number) => (
                              <li key={idx} style={{ fontSize: '14px', color: '#475569', marginBottom: '2px' }}>{move}</li>
                            ))}
                          </ul>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 4px 0' }}>Reporting Line:</h4>
                          <div style={{ fontSize: '14px', color: '#475569' }}>{brief.reportingLine}</div>
                        </div>
                        <div>
                          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 4px 0' }}>Escalates When:</h4>
                          <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            {brief.escalatesWhen?.map((condition: string, idx: number) => (
                              <li key={idx} style={{ fontSize: '14px', color: '#475569', marginBottom: '2px' }}>{condition}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>
                    No role briefs generated for this exercise
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Site Participant Roles - shown when Role Briefs are hidden */}
          {selectedSection === 'roleBriefs' && !showRoleBriefs && (
            <Card style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
                <CardTitle style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#111827',
                  margin: '0'
                }}>Participant Roles (Site)</CardTitle>
              </CardHeader>
              <CardContent style={{ padding: '0 24px 24px 24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {getSiteParticipantRoles().map((role, index) => (
                    <div key={index} style={{
                      padding: '12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <h3 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1e293b',
                        margin: '0 0 8px 0'
                      }}>{role.role}</h3>
                      <p style={{ 
                        margin: '0', 
                        fontSize: '13px', 
                        color: '#475569',
                        lineHeight: '1.4'
                      }}>
                        {role.purpose}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {selectedSection === 'scripts' && (
            <Card style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
                <CardTitle style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#111827',
                  margin: '0'
                }}>Facilitator Scripts</CardTitle>
              </CardHeader>
              <CardContent style={{ padding: '0 24px 24px 24px' }}>
{exercise.sections?.facilitatorScripts && exercise.sections.facilitatorScripts.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {exercise.sections.facilitatorScripts.map((rawScript: any, index: number) => {
                      const script = getEnhancedFacilitatorScript(rawScript);
                      const isEnhanced = hasEnhancedFields(script);
                      
                      return (
                        <div key={index} style={{
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          overflow: 'hidden'
                        }}>
                          {/* Header */}
                          <div style={{
                            padding: '16px',
                            backgroundColor: isEnhanced ? '#3b82f6' : '#6b7280',
                            color: 'white'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h3 style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                margin: '0'
                              }}>
                                {script.slideTitle || script.phase}
                              </h3>
                              <span style={{
                                fontSize: '12px',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}>
                                {getDisplayTiming(script)}
                              </span>
                            </div>
                            {script.purpose && (
                              <p style={{
                                fontSize: '13px',
                                margin: '8px 0 0 0',
                                opacity: 0.9
                              }}>
                                {script.purpose}
                              </p>
                            )}
                          </div>

                          {/* Read Aloud (Verbatim Text) */}
                          <div style={{
                            padding: '16px',
                            backgroundColor: '#f1f5f9',
                            borderBottom: '1px solid #e2e8f0'
                          }}>
                            <h4 style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#475569',
                              margin: '0 0 8px 0',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Verbatim Slide Text</h4>
                            <div style={{
                              fontSize: '14px',
                              color: '#1e293b',
                              lineHeight: '1.6',
                              whiteSpace: 'pre-wrap',
                              fontStyle: 'italic'
                            }}>
                              "{script.readAloud}"
                            </div>
                          </div>

                          {/* Enhanced Content */}
                          <div style={{ padding: '16px' }}>
                            <div style={{ display: 'grid', gap: '16px' }}>
                              
                              {/* Expected Decisions */}
                              {script.expectedDecisions && script.expectedDecisions.length > 0 && (
                                <div>
                                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Expected Decisions (inc. thresholds):</h4>
                                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                                    {script.expectedDecisions.map((decision: string, idx: number) => (
                                      <li key={idx} style={{ fontSize: '14px', color: '#475569', marginBottom: '4px' }}>{decision}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Unknowns to Surface */}
                              {script.unknownsToSurface && script.unknownsToSurface.length > 0 && (
                                <div>
                                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Unknowns to Surface (Rule of Five #2):</h4>
                                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                                    {script.unknownsToSurface.map((unknown: string, idx: number) => (
                                      <li key={idx} style={{ fontSize: '14px', color: '#dc2626', marginBottom: '4px' }}>{unknown}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Probes */}
                              {(script.probesIfStall || script.ifTheyStall) && (
                                <div>
                                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Probes if Stalled:</h4>
                                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                                    {(script.probesIfStall || script.ifTheyStall)?.map((probe: string, idx: number) => (
                                      <li key={idx} style={{ fontSize: '14px', color: '#475569', marginBottom: '4px' }}>{probe}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Curveballs */}
                              {script.curveballs && script.curveballs.length > 0 && (
                                <div>
                                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Curveballs:</h4>
                                  <div style={{ display: 'grid', gap: '8px' }}>
                                    {script.curveballs.map((curveball: any, idx: number) => (
                                      <div key={idx} style={{
                                        padding: '8px 12px',
                                        backgroundColor: '#fef3c7',
                                        border: '1px solid #f59e0b',
                                        borderRadius: '4px'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                          <span style={{ fontSize: '14px', color: '#92400e', flex: 1 }}>
                                            {typeof curveball === 'string' ? curveball : curveball.text}
                                          </span>
                                          {typeof curveball === 'object' && (
                                            <span style={{
                                              fontSize: '12px',
                                              fontWeight: '600',
                                              color: '#92400e',
                                              marginLeft: '8px'
                                            }}>
                                              {getCurveballTiming(curveball)}
                                            </span>
                                          )}
                                        </div>
                                        {typeof curveball === 'object' && curveball.intent && (
                                          <div style={{ fontSize: '12px', color: '#92400e', marginTop: '4px', fontStyle: 'italic' }}>
                                            Intent: {curveball.intent}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Capture Instructions */}
                              {script.captureInstructions && script.captureInstructions.length > 0 && (
                                <div>
                                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Capture Instructions:</h4>
                                  <div style={{ display: 'grid', gap: '6px' }}>
                                    {script.captureInstructions.map((instruction: any, idx: number) => (
                                      <div key={idx} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '14px'
                                      }}>
                                        <span style={{
                                          backgroundColor: '#10b981',
                                          color: 'white',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          fontWeight: '600'
                                        }}>
                                          {instruction.target}
                                        </span>
                                        <span style={{ color: '#475569' }}>{instruction.what}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Handoff */}
                              {script.handoffToNext && (
                                <div style={{
                                  padding: '12px',
                                  backgroundColor: '#e0f2fe',
                                  border: '1px solid #0284c7',
                                  borderRadius: '4px'
                                }}>
                                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e', margin: '0 0 6px 0' }}>Handoff to Next:</h4>
                                  <div style={{ fontSize: '14px', color: '#0c4a6e' }}>{script.handoffToNext}</div>
                                </div>
                              )}

                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>
                    No facilitator scripts generated for this exercise
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {selectedSection === 'debrief' && (
            <Card style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
                <CardTitle style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#111827',
                  margin: '0'
                }}>Debrief Prompts</CardTitle>
              </CardHeader>
              <CardContent style={{ padding: '0 24px 24px 24px' }}>
                {exercise.sections?.debriefPrompts && exercise.sections.debriefPrompts.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {exercise.sections.debriefPrompts.map((prompt: string, index: number) => (
                      <div key={index} style={{
                        padding: '16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{
                          fontSize: '14px',
                          color: '#475569',
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap'
                        }}>
                          <strong style={{ color: '#1e293b' }}>Q{index + 1}:</strong> {prompt}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>
                    No debrief prompts generated for this exercise
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <Trash2 style={{ width: '24px', height: '24px', color: '#dc2626', marginRight: '12px' }} />
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>
                Delete Exercise
              </h3>
            </div>
            
            <p style={{ fontSize: '16px', color: '#374151', margin: '0 0 24px 0', lineHeight: '1.5' }}>
              Are you sure you want to delete <strong>"{exercise?.exerciseTitle || 'this exercise'}"</strong>? 
              This action cannot be undone and all exercise data will be permanently removed.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </Button>
              
              <Button
                onClick={handleDeleteExercise}
                disabled={deleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: deleting ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Trash2 style={{ width: '16px', height: '16px' }} />
                {deleting ? 'Deleting...' : 'Delete Exercise'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

